<script setup lang="ts">
import { computed } from 'vue';
import type { ToolbarItem } from '@alphadoc/alphadoc-text-editor';
import { createSchema } from '@/modules/ui/models/schema/schema.ts';
import { preloadTextEditor, SchemaTextEditor } from '@/modules/ui/models/schema/schema-text-editor.ts';
import { useWizardStep } from '@/providers/wizard.ts';
import { useCounterpartyMaster } from '@/modules/counterparty/counterparty-master/counterparty-master.ts';
import { CounterpartyExecutionTaskCode } from '@/modules/counterparty/counterparty-master/enums/execution-task-code.ts';
import { COUNTERPARTY_COMMENT_MAX_LENGTH } from '@/modules/counterparty/counterparty-master/types/comment-form.ts';
import UiForm from '@/modules/ui/components/ui/UiForm.vue';

preloadTextEditor();

const schema = createSchema(
  Object.assign(
    new SchemaTextEditor({
      name: 'comment',
      placeholder: 'document.ui.common.placeholder.enterComment',
      maxLength: COUNTERPARTY_COMMENT_MAX_LENGTH,
    }),
    {
      toolbarItems: [
        'textStyle',
        'bold',
        'italic',
        'underline',
        'strike',
        'link',
        'lists',
        'blockquote',
        'codeBlock',
        'horizontalRule',
        'undo',
        'redo',
      ] satisfies ToolbarItem[],
    }
  )
);

const { formData, completedTaskCodes } = useCounterpartyMaster();
const { isValid, showErrors } = useWizardStep();

const formModel = computed({
  get: () => ({ comment: formData.value.comment }),
  set: ({ comment }: { comment: string }) => {
    formData.value = {
      ...formData.value,
      comment,
    };
    completedTaskCodes.delete(CounterpartyExecutionTaskCode.Comment);
  },
});
</script>

<template>
  <UiForm
    v-model="formModel"
    v-model:valid="isValid"
    v-model:show-errors="showErrors"
    class="step-comment"
    type="compact"
    :schema
  />
</template>

<style scoped lang="scss">
.step-comment {
  display: flex;
  height: 100%;
  min-width: 0;
  min-height: 0;
  flex-direction: column;

  > :deep(*) {
    display: flex;
    flex: 1 1 auto;
    min-width: 0;
    min-height: 0;
    flex-direction: column;
  }

  :deep(.ui-form__fields) {
    display: flex;
    flex: 1 1 auto;
    min-height: 0;
    flex-direction: column;
  }

  :deep(.ui-field) {
    display: flex;
    flex: 1 1 auto;
    min-width: 0;
    min-height: 0;
    max-width: 100%;
    flex-direction: column;
  }

  :deep(.ui-tooltip) {
    display: flex;
    flex: 1 1 auto;
    min-width: 0;
    min-height: 0;
    height: 100%;
    flex-direction: column;
  }

  :deep(.ui-text-editor) {
    flex: 1 1 auto;
    height: 100%;
    min-width: 0;
    min-height: 0;

    --alpha-editor-height: 100%;
    --alpha-editor-min-height: 100%;
    --alpha-editor-max-height: 100%;
  }

  :deep(.resize-handle) {
    display: none;
  }
}
</style>
